<template>
  <div class="global-filter-menu">
    <Chips
      v-model="translatedFilters"
      :max-printed-values="maxPrintedValues"
      :show-nonvisible-filter-counter="showNonvisibleFilterCounter"
      :read-only="readOnly"
      @remove="removeAction"
    />
  </div>
</template>

<script>
import Chips from '@/components/Chips';
import { MUTATE } from '@/store/modules/mutation_types';
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import { FILTER_NAMES, DATA_TYPE } from '@/store/modules/cards/constants';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import repliersModel from '@/mixins/repliers-model';
import sentimentModel from '@/mixins/sentiment-model';
import darkpostModel from '@/mixins/darkpost-model';
import channelModel from '@/mixins/channel-model';
import sacTypeModel from '@/mixins/sac-type-model';
import blipModel from '@/mixins/blip-model';

import { NORMALIZED_MIN_PATTERN_SLASHED } from '@/utils/date';
import { groupDuplicatedUsers } from '@/services/WarRoomService';
import cloneDeep from 'lodash/cloneDeep';

export default {
  name: 'GlobalFilterMenu',
  components: {
    Chips,
  },
  mixins: [
    repliersModel,
    sentimentModel,
    darkpostModel,
    channelModel,
    sacTypeModel,
    blipModel,
  ],
  props: {
    showNonvisibleFilterCounter: {
      type: Boolean,
      default: false,
    },
    showDateRange: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    dataType: {
      type: String,
      default: null,
    },
    globalFilterByDataType: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      actualFilters: {},
      selectedFilters: {},
      translatedFilters: {},
      paramsMap: {},
      maxPrintedValues: Number(1),
      filtersMap: {
        names: 'Grupos',
        themes: 'Temas',
        tags: 'Tags',
        polarities: 'Sentimento',
        dark_post: 'Tipo de Publicação',
        channels_listening: 'Canais',
        channels: 'Canais',
        uids: 'Autores',
        pages: 'Páginas',
        sac_type: 'Interações',
        sac_status: 'Status',
        sac_replier: 'Respondentes',
        contracts: 'Contrato',
        bots: 'Bot',
        category: 'Categoria',
      },
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'activeGlobalFilter',
      'activeDashboardFilters',
      'pages',
    ]),
    ...mapState('filter', [
      'globalFilter',
      'dateRange',
    ]),
  },
  watch: {
    activeGlobalFilter(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      this.loadValues(newValue);
    },
  },
  created() {
    this.paramsMap = {
      polarities: this.sentimentList,
      dark_post: this.darkpostList,
      channels_listening: this.channelsList,
      channels: this.channelsList,
      sac_type: this.sacTypeList,
    };
  },
  mounted() {
    if (this.activeGlobalFilter && Boolean(Object.entries(this.activeGlobalFilter))) {
      this.loadValues(this.activeGlobalFilter);
      return;
    }

    this.loadValues(this.globalFilterByDataType || []);
  },
  methods: {
    ...mapMutations('filter', {
      mutateFilter: MUTATE,
    }),
    ...mapActions('dashboard', [
      'saveGlobalFilter',
    ]),
    orderedFilters() {
      return Object.keys(this.translatedFilters).sort().reduce(
        (obj, key) => {
          obj[key] = this.translatedFilters[key];
          return obj;
        },
        {}
      );
    },
    checkHasSacTypeAll(value) {
      return value.hasOwnProperty(FILTER_NAMES.SAC_TYPE) ? Boolean(value.sac_type.value[0] === 'All') : false;
    },
    sanatize() {
      // This atribute should not use
      if (this.dataType !== DATA_TYPE.LISTENING && this.dataType !== DATA_TYPE.COMPETITIVE) {
        delete this.selectedFilters.channels;
      }
    },
    translateFilters() {
      const filtersTranslated = new Map();

      new Map(Object.entries(this.selectedFilters)).forEach((value, key) => {
        const referenceKey = key;
        let referenceValue = value.value;
        const translatedKey = this.filtersMap[referenceKey];

        // Sac replier has a different none selected values representation
        if (!referenceValue?.length && referenceKey === FILTER_NAMES.SAC_REPLIER) {
          filtersTranslated.set(translatedKey, ['Sem respondentes']);
          return;
        }

        // When filters has more than one parameter or when filter does not need translate
        if (this.isMoreThenOneValue(value)) {
          filtersTranslated.set(translatedKey, referenceValue);
          return;
        }
        // For cases when filters has only one parameter like 'dark_post'
        if (!Array.isArray(value.value)) {
          referenceValue = [value.value];
        }

        let translatedParams = '';

        if (this.paramsMap.hasOwnProperty(referenceKey)) {
          translatedParams = this.paramsMap[referenceKey][referenceValue];
        } else {
          filtersTranslated.set(translatedKey, referenceValue);
        }

        this.insertValue(filtersTranslated, translatedKey, translatedParams);
      });

      return Object.fromEntries(filtersTranslated);
    },
    insertValue(filtersTranslated, translatedKey, translatedParams) {
      if (!translatedParams) {
        return;
      }

      filtersTranslated.set(translatedKey, [translatedParams]);
    },
    setContractsAndBots() {
      if (this.activeGlobalFilter?.hasOwnProperty(FILTER_NAMES.CONTRACTS)) {
        const contractMap = this.contractFilterItems?.map(contract => {
          return [contract.value, contract.text];
        });

        this.paramsMap.contracts = Object.fromEntries(contractMap);
      }

      if (this.activeGlobalFilter?.hasOwnProperty(FILTER_NAMES.BOTS)) {
        const botMap = this.allBotFilterItems?.map(bot => {
          return [bot.value, bot.text];
        });

        this.paramsMap.bots = Object.fromEntries(botMap);
      }
    },
    loadValues(filters) {
      const hasSacTypeAll = this.checkHasSacTypeAll(filters);
      if (hasSacTypeAll) {
        delete filters.sac_type;
      }
      this.actualFilters = filters;
      this.selectedFilters = cloneDeep(this.actualFilters);

      this.sanatize();

      if (this.activeGlobalFilter?.hasOwnProperty(FILTER_NAMES.SAC_REPLIER)) {
        const sacReplierMap = this.activeDashboardFilters?.repliers.map(replier => {
          return [replier.id, replier.name];
        });

        this.paramsMap.sac_replier = Object.fromEntries(sacReplierMap);
      }

      if (this.activeGlobalFilter?.hasOwnProperty(FILTER_NAMES.PAGES)) {
        const pagesMap = this.pages?.map(page => {
          return [page.page_id, page.title];
        });

        this.paramsMap.pages = Object.fromEntries(pagesMap);
      }

      if (this.selectedFilters.hasOwnProperty(FILTER_NAMES.UIDS)) {
        const groupedAuthors = groupDuplicatedUsers(this.selectedFilters.uids.users);
        this.selectedFilters.uids.value = groupedAuthors.map(author => author.uid);

        const authorsMap = groupedAuthors.map(author => {
          return [author.uid, author.username];
        });

        this.paramsMap.uids = Object.fromEntries(authorsMap);
      }

      this.setContractsAndBots();

      this.translatedFilters = this.translateFilters();
      this.translatedFilters = this.orderedFilters();

      if (this.showDateRange) {
        const dateRangeObj = { 'Intervalo de tempo': [`${this.dateRange.start.format(NORMALIZED_MIN_PATTERN_SLASHED)} - ${this.dateRange.end.format(NORMALIZED_MIN_PATTERN_SLASHED)}`] };

        this.translatedFilters = { ...dateRangeObj, ...this.translatedFilters };
      }
    },
    removePropertyFilter(value, key, translatedKey) {
      if (key === FILTER_NAMES.CONTRACTS && value === translatedKey) {
        delete this.actualFilters[FILTER_NAMES.CONTRACTS];
        delete this.actualFilters[FILTER_NAMES.BOTS];
        return;
      }

      if (value === translatedKey) delete this.actualFilters[key];
    },
    removeAction(translatedKey) {
      new Map(Object.entries(this.filtersMap)).forEach((value, key) => this.removePropertyFilter(value, key, translatedKey));

      if (translatedKey === 'Páginas') {
        delete this.actualFilters.channels;
      }

      this.selectedFilters = cloneDeep(this.actualFilters);

      this.mutateFilter({
        property: 'globalFilter',
        with: this.selectedFilters,
      });

      this.saveGlobalFilter(this.selectedFilters);

      trackEventWithUserProperties('ST GlobalFilter Bar Delete', { Field: translatedKey });
    },
    isMoreThenOneValue(value) {
      return (Array.isArray(value.value) && value.value.length > 1);
    },
  },
};
</script>

<style scoped lang="scss">
  .global-filter-menu {
    display: flex;
    padding: 10px var(--ds-spacing-stack-quark) 0 var(--ds-spacing-stack-quark);
    width: auto;
    max-width: calc(100vw - 494px);
  }
</style>
